/**
 * WidgetAppearanceConfig - Configuration d'apparence des widgets
 * 
 * Options communes à tous les widgets :
 * - Couleur de fond et opacité
 * - Affichage de l'en-tête (titre, icône, position)
 */

import React from "react";
import { Typography, Switch, Select, Option } from "@/components/ui";
import { FaPaintBrush } from "react-icons/fa";
import type { AppearanceConfig } from "@/models/widget-v2";
import ColorPicker from "@/components/layout/arc-sidebar/color-picker";

// ============================================================================
// Types
// ============================================================================

interface WidgetAppearanceConfigProps {
  config: AppearanceConfig;
  onChange: (config: AppearanceConfig) => void;
}

// ============================================================================
// Constants
// ============================================================================

const HEADER_POSITIONS = [
  { value: "top", label: "En haut" },
  { value: "bottom", label: "En bas" },
] as const;

const OPACITY_PRESETS = [
  { value: 100, label: "100% (Opaque)" },
  { value: 80, label: "80%" },
  { value: 60, label: "60%" },
  { value: 40, label: "40%" },
  { value: 20, label: "20%" },
  { value: 0, label: "0% (Transparent)" },
] as const;

// ============================================================================
// Main Component
// ============================================================================

export const WidgetAppearanceConfig: React.FC<WidgetAppearanceConfigProps> = ({
  config,
  onChange,
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 text-content-secondary">
        <FaPaintBrush size={16} />
        <Typography variant="text-sm" className="font-semibold">
          Apparence du widget
        </Typography>
      </div>
      
      {/* Section: Fond */}
      <div className="space-y-4">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Fond
        </Typography>
        
        <div className="space-y-4">
          {/* Couleur de fond */}
          <div className="p-3 bg-arc-surface-secondary rounded-lg">
            <ColorPicker
              value={config.backgroundColor}
              onChange={(color) => onChange({ ...config, backgroundColor: color })}
              label="Couleur de fond"
            />
          </div>
          
          {/* Opacité */}
          <div className="space-y-2">
            <Typography variant="text-xs" className="text-content-tertiary">
              Opacité du fond
            </Typography>
            <Select
              value={String(config.backgroundOpacity ?? 100)}
              onChange={(value) => onChange({ ...config, backgroundOpacity: Number(value) })}
            >
              {OPACITY_PRESETS.map((preset) => (
                <Option key={preset.value} value={String(preset.value)}>
                  {preset.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>
      
      {/* Section: En-tête */}
      <div className="space-y-4 pt-4 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          En-tête
        </Typography>
        
        <div className="space-y-3">
          {/* Afficher l'en-tête */}
          <Switch
            label="Afficher l'en-tête"
            checked={config.showHeader ?? false}
            onChange={(checked) => onChange({ ...config, showHeader: checked })}
          />
          
          {/* Options conditionnelles si l'en-tête est affiché */}
          {(config.showHeader ?? false) && (
            <div className="pl-4 space-y-3 border-l-2 border-arc-border">
              {/* Afficher l'icône */}
              <Switch
                label="Afficher l'icône"
                checked={config.showIcon ?? true}
                onChange={(checked) => onChange({ ...config, showIcon: checked })}
              />
              
              {/* Position de l'en-tête */}
              <Select
                label="Position de l'en-tête"
                value={config.headerPosition || "top"}
                onChange={(value) => onChange({ 
                  ...config, 
                  headerPosition: value as AppearanceConfig["headerPosition"] 
                })}
              >
                {HEADER_POSITIONS.map((pos) => (
                  <Option key={pos.value} value={pos.value}>
                    {pos.label}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetAppearanceConfig;
